import { useEffect, useState } from "react";

import {
  ENTRY_DATA_SECTION_KEYS,
  VENTURE_FORMATION_SHELL_KEYS,
  VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS,
} from "@frontend/common";

import { ArticleEntryData } from "@/types/articleEntryData.types";
import { DealsSection } from "@/types/deals.types";
import { useSellerAndBuyerByDealTypeQuery } from "@api/sellerAndBuyerByDealType/sellerAndBuyerByDealType";
import { useScroll } from "@FillInTableContainer/components/FillInTable/hooks/useScroll";
import useArticleSection from "@FillInTableContainer/hooks/useArticleSection";
import {
  DefaultShellTitle,
  TableCreatorTextFieldContainer,
} from "@FillInTableContainer/shared";
import { useCurrentDealTypeValue } from "@FillInTableContainer/shared/context/CurrentDealTypeContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import useCheckPressedShell from "@FillInTableContainer/shared/hooks/useCheckPressedShell";
import { useGetInfoOfShell } from "@FillInTableContainer/shared/hooks/useGetInfoOfShell";
import {
  getShellPlaceholder,
  isRequiredShell,
} from "@FillInTableContainer/shared/utils/getOptionsFromData";
import { getShellTitle } from "@FillInTableContainer/shared/utils/getTextFromData";
import IconXMark from "@images/fillInTable/tableCreator/icon-x-mark-white.svg";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";
import { padStart } from "@utils/padStart/padStart";

import VentureFormationTerritoryControlledModal from "./components/VentureFormationTerritoryControlledModal/VentureFormationTerritoryControlledModal";
import * as S from "./DealTermsMergeShell.style";
import {
  ASSET_TRANSFER_COLUMN_WIDTH,
  ASSET_TRANSFER_SHELL_KEYS,
} from "../../../../constants/assetTransferColumns.constants";
import { useHandleDealTermsMergeTableCreatorShell } from "./hooks/useHandleDealTermsMergeTableCreatorShell";
import { useGetNumberOfSupGroupShell } from "../../../../../../../../../DealTermsLicenseAgreementGroupTable/components/DealTermsLicenseAgreementTable/components/DealTermsLicenseAgreementGroupShellSection/components/DealTermsLicenseAgreementGroupShell/hooks/useGetNumberOfSubGroupShell";

interface Props {
  groupTableKey: DealsSection;
  groupTableIndex: number;
  groupShellKey: string | null;
  groupShellIndex: number;
  subGroupShellIndex: number;
  subGroupShellKey: string | null;
  tableKey: string | null;
  tableIndex: number;
  shellKey: string;
  shellIndex: number;
  isFocusedGroupShell: boolean;
  isDeleteGroupShell: boolean;
  isClearGroupShell: boolean;
  isAlertGroupShell: boolean;
  isClearSubGroupShell: boolean;
  isDeleteSubGroupShell: boolean;
  isClearGroupShellSection?: boolean;
}

interface PositionInfoProps {
  groupTableKey: DealsSection;
  groupTableIndex: number;
  groupShellKey: string | null;
  groupShellIndex: number;
  subGroupShellIndex: number;
  subGroupShellKey: string | null;
  tableKey: string | null;
  tableIndex: number;
  shellKey: string;
  shellIndex: number;
}

const DealTermsMergeShell = ({
  groupShellIndex,
  groupShellKey,
  subGroupShellIndex,
  subGroupShellKey,
  groupTableIndex,
  groupTableKey,
  tableIndex,
  tableKey,
  shellKey,
  shellIndex,
  isAlertGroupShell,
  isDeleteGroupShell,
  isClearGroupShell,
  isClearSubGroupShell,
  isDeleteSubGroupShell,
  isClearGroupShellSection,
}: Props) => {
  const positionPropsInfo: PositionInfoProps = {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    subGroupShellIndex,
    subGroupShellKey,
    tableKey,
    tableIndex,
    shellKey,
    shellIndex,
  };

  const {
    isFocused,
    isClearShell,
    isEnableEdit,
    isAlertShell,
    focusStatus,
    columnSize,
    handleFocusTextFieldWrapper,
    handleClickToDeleteShell,
    handleFocusOutShell,
  } = useHandleDealTermsMergeTableCreatorShell(positionPropsInfo);
  const { isPressed, handleChangePressedStatus } = useCheckPressedShell();
  const { subGroupShellCount } = useGetNumberOfSupGroupShell();

  const articleSection = useArticleSection();
  const { shellValue } = useGetInfoOfShell();
  const { register } = useScroll();

  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const [isOpenTerritoryControlledModal, setIsOpenTerritoryControlledModal] =
    useState(false);

  const { currentDealTypeList } = useCurrentDealTypeValue();
  const { data: sellerBuyerInfo } =
    useSellerAndBuyerByDealTypeQuery(currentDealTypeList);

  const hasMultipleContingentPaymentSubGroupShell = () => {
    const currentContingentPaymentLength = subGroupShellCount(
      subGroupShellKey,
      tableIndex,
      groupShellIndex,
    );

    if (currentContingentPaymentLength > 1) return true;

    return false;
  };

  const getMergeSellTitle = () => {
    if (
      subGroupShellKey ===
        VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS.contingentInvestment &&
      hasMultipleContingentPaymentSubGroupShell()
    )
      return `${getShellTitle(articleSection, shellKey)} ${padStart(
        subGroupShellIndex + 1,
      )}`;

    // Advisor Table 행이 2개 이상이면 LA&SC 처럼 쉘 타이틀에 번호를 붙인다.
    // (예: "Advisor Name 01")
    if (
      subGroupShellKey === "Advisor" &&
      subGroupShellCount("Advisor", tableIndex, groupShellIndex) > 1
    )
      return `${getShellTitle(articleSection, shellKey)} ${padStart(
        subGroupShellIndex + 1,
      )}`;

    if (shellKey === VENTURE_FORMATION_SHELL_KEYS.licensorName)
      return sellerBuyerInfo.data.seller;

    if (shellKey === VENTURE_FORMATION_SHELL_KEYS.licenseeName)
      return sellerBuyerInfo.data.buyer;

    // if (shellKey === "Drug") return "Drug Assets";

    return getShellTitle(articleSection, shellKey);
  };

  useEffect(() => {
    const handleCheckClickOutsideShell = () => {
      if (isFocused) handleFocusOutShell();
    };

    window.addEventListener("click", handleCheckClickOutsideShell);

    return () =>
      window.removeEventListener("click", handleCheckClickOutsideShell);
  }, [handleFocusOutShell, isFocused]);

  // VF Asset Transfer 의 Territory Normalized(Include/Exclude)는
  // 텍스트 직접 입력이 아니라 controlled modal 로 값을 채운다. (LA&SC 동일)
  const TERRITORY_NORMALIZED_SHELL_KEYS = [
    "Territory Include",
    "Territory Exclude",
  ];
  if (TERRITORY_NORMALIZED_SHELL_KEYS.includes(shellKey)) {
    const shellValuePlaceholder = getShellTitle(articleSection, shellKey);
    const shellValueText = shellValue(
      shellKey,
      tableIndex,
      groupShellIndex,
      shellIndex,
      subGroupShellIndex,
    )
      .value.map(({ text }) => text)
      .join(", ");
    const isShellValue = !!shellValueText;

    return (
      <S.AssetTransferShellWrapper
        column={columnSize}
        $width={ASSET_TRANSFER_COLUMN_WIDTH[shellKey] || "12.8rem"}
        {...register(
          `${groupTableKey}__${groupTableIndex}__${tableKey}__${tableIndex}__${groupShellKey}__${groupShellIndex}__${shellKey}__${shellIndex}`,
        )}
      >
        <S.ClickableShellBorderWrapper
          onClick={() => setIsOpenTerritoryControlledModal(true)}
          $isModified={shellValue(
            shellKey,
            tableIndex,
            groupShellIndex,
            shellIndex,
            subGroupShellIndex,
          ).value.some(({ is_modified }) => is_modified)}
          $isPlaceholder={!isShellValue}
        >
          {isShellValue ? shellValueText : shellValuePlaceholder}
        </S.ClickableShellBorderWrapper>
        {isOpenTerritoryControlledModal && (
          <VentureFormationTerritoryControlledModal
            onClose={() => setIsOpenTerritoryControlledModal(false)}
            selectedInclude={shellValue(
              "Territory Include",
              tableIndex,
              groupShellIndex,
              shellIndex,
              subGroupShellIndex,
            ).value.flatMap(({ text }) => text.split(", "))}
            selectedExclude={shellValue(
              "Territory Exclude",
              tableIndex,
              groupShellIndex,
              shellIndex,
              subGroupShellIndex,
            ).value.flatMap(({ text }) => text.split(", "))}
            saveSelectedData={(
              selectedInclude: string[],
              selectedExclude: string[],
            ) => {
              const changedEntryData = {
                ...entryData,
                [ENTRY_DATA_SECTION_KEYS.deals]: {
                  ...entryData?.[ENTRY_DATA_SECTION_KEYS.deals],
                  "Licensee Table": entryData?.[
                    ENTRY_DATA_SECTION_KEYS.deals
                  ]?.["Licensee Table"]?.map((tableItem, tableItemIndex) => {
                    if (tableItemIndex !== tableIndex) return tableItem;
                    return {
                      ...tableItem,
                      "Rights Scope": tableItem["Rights Scope"]?.map(
                        (rightsScopeItem, rightsScopeIndex) => {
                          if (rightsScopeIndex !== groupShellIndex)
                            return rightsScopeItem;
                          return {
                            ...rightsScopeItem,
                            "Territory Include": {
                              ...rightsScopeItem?.["Territory Include"],
                              value: selectedInclude.map((include) => ({
                                text: include,
                                is_modified: true,
                              })),
                            },
                            "Territory Exclude": {
                              ...rightsScopeItem?.["Territory Exclude"],
                              value: selectedExclude.map((exclude) => ({
                                text: exclude,
                                is_modified: true,
                              })),
                            },
                          };
                        },
                      ),
                    };
                  }),
                },
              } as ArticleEntryData;
              setEntryData(() => changedEntryData);

              changeCurrentHistory({
                entryData: changedEntryData,
                changedDataInfo: {
                  target: "value",
                  action: "update",
                  position: {
                    groupTableKey,
                    groupTableIndex,
                    tableKey,
                    tableIndex,
                    groupShellKey,
                    groupShellIndex,
                    shellKey,
                    shellIndex,
                    value: null,
                    valueIndex: null,
                  },
                },
              });
            }}
          />
        )}
      </S.AssetTransferShellWrapper>
    );
  }

  // Asset Transfer 의 텍스트 셀(Drug/Indication/Exclusivity/Territory)은
  // 헤더 행이 컬럼명을 제공하므로 셀 타이틀 없이 고정 폭으로 렌더한다.
  const isAssetTransferTextCell =
    groupShellKey === "Rights Scope" &&
    ASSET_TRANSFER_SHELL_KEYS.includes(shellKey);

  if (isAssetTransferTextCell) {
    return (
      <S.AssetTransferShellWrapper
        column={columnSize}
        $width={ASSET_TRANSFER_COLUMN_WIDTH[shellKey] || "14rem"}
        {...register(
          `${groupTableKey}__${groupTableIndex}__${tableKey}__${tableIndex}__${groupShellKey}__${groupShellIndex}__${shellKey}__${shellIndex}`,
        )}
      >
        {isClearShell && isEnableEdit && (
          <S.DeleteTableButtonWrapper>
            <S.DeleteTableButton
              type="button"
              onClick={handleClickToDeleteShell}
            >
              <img src={IconXMark} alt="button for clear" />
              {focusStatus.type === "clear" ? "Clear" : null}
            </S.DeleteTableButton>
          </S.DeleteTableButtonWrapper>
        )}
        <S.AssetTransferShellBorderWrapper
          isFocused={isFocused && focusStatus.type !== "dragging"}
          isClearStatus={
            isClearShell ||
            isClearGroupShell ||
            isClearSubGroupShell ||
            !!isClearGroupShellSection
          }
          isDeleteStatus={isDeleteGroupShell || isDeleteSubGroupShell}
          isAlertStatus={isAlertShell || isAlertGroupShell}
          isDisableEdit={!isEnableEdit}
          $isPressed={isPressed}
          $isDragging={isFocused && focusStatus.type === "dragging"}
          onClick={handleFocusTextFieldWrapper}
          onMouseDown={(e) => handleChangePressedStatus(e, true)}
          onMouseUp={(e) => handleChangePressedStatus(e, false)}
        >
          <TableCreatorTextFieldContainer
            placeholder={
              getShellPlaceholder(articleSection, shellKey) ||
              getShellTitle(articleSection, shellKey)
            }
            shellValue={shellValue(
              shellKey,
              tableIndex,
              groupShellIndex,
              shellIndex,
              subGroupShellIndex,
            )}
            shellKey={shellKey}
            shellIndex={shellIndex}
            groupTableKey={groupTableKey}
            groupTableIndex={groupTableIndex}
            groupShellKey={groupShellKey}
            groupShellIndex={groupShellIndex}
            subGroupShellIndex={subGroupShellIndex}
            subGroupShellKey={subGroupShellKey}
            tableKey={tableKey}
            tableIndex={tableIndex}
          />
        </S.AssetTransferShellBorderWrapper>
      </S.AssetTransferShellWrapper>
    );
  }

  return (
    <S.ShellWrapper
      column={columnSize}
      {...register(
        `${groupTableKey}__${groupTableIndex}__${tableKey}__${tableIndex}__${groupShellKey}__${groupShellIndex}__${shellKey}__${shellIndex}`,
      )}
    >
      {isClearShell && isEnableEdit && (
        <S.DeleteTableButtonWrapper>
          <S.DeleteTableButton type="button" onClick={handleClickToDeleteShell}>
            <img src={IconXMark} alt="button for clear" />
            {focusStatus.type === "clear" ? "Clear" : null}
          </S.DeleteTableButton>
        </S.DeleteTableButtonWrapper>
      )}

      <S.ShellBorderWrapper
        isFocused={isFocused && focusStatus.type !== "dragging"}
        isClearStatus={
          isClearShell ||
          isClearGroupShell ||
          isClearSubGroupShell ||
          !!isClearGroupShellSection
        }
        isDeleteStatus={isDeleteGroupShell || isDeleteSubGroupShell}
        isAlertStatus={isAlertShell || isAlertGroupShell}
        isDisableEdit={!isEnableEdit}
        $isPressed={isPressed}
        $isDragging={isFocused && focusStatus.type === "dragging"}
        onClick={handleFocusTextFieldWrapper}
        onMouseDown={(e) => handleChangePressedStatus(e, true)}
        onMouseUp={(e) => handleChangePressedStatus(e, false)}
      >
        <DefaultShellTitle
          isFocused={isFocused}
          isClearShell={isClearShell}
          isAlertShell={isAlertShell}
          isRequiredShell={isRequiredShell(articleSection, shellKey)}
          shellTitle={getMergeSellTitle()}
          columnSize={columnSize}
        />

        <TableCreatorTextFieldContainer
          placeholder={
            getShellPlaceholder(articleSection, shellKey) ||
            getShellTitle(articleSection, shellKey)
          }
          shellValue={shellValue(
            shellKey,
            tableIndex,
            groupShellIndex,
            shellIndex,
            subGroupShellIndex,
          )}
          shellKey={shellKey}
          shellIndex={shellIndex}
          groupTableKey={groupTableKey}
          groupTableIndex={groupTableIndex}
          groupShellKey={groupShellKey}
          groupShellIndex={groupShellIndex}
          subGroupShellIndex={subGroupShellIndex}
          subGroupShellKey={subGroupShellKey}
          tableKey={tableKey}
          tableIndex={tableIndex}
        />
      </S.ShellBorderWrapper>
    </S.ShellWrapper>
  );
};

export default DealTermsMergeShell;
